import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, FileText, Plus, ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import FormFiller from './FormFiller';

/**
 * computeFormStatus — derive a single visual status for a form type from its
 * instances. Filled/Signed = green; everything pending = gray.
 */
function computeFormStatus(tplInstances) {
  if (!tplInstances || tplInstances.length === 0) {
    return { filled: false, label: 'Pending', badgeClass: 'bg-gray-50 border-gray-200 text-gray-500' };
  }
  const statuses = tplInstances.map(i => (i.status || '').toLowerCase());
  if (statuses.includes('granted')) {
    return { filled: true, label: 'Granted', badgeClass: 'bg-emerald-50 border-emerald-200 text-emerald-700' };
  }
  if (statuses.includes('signed')) {
    return { filled: true, label: 'Signed', badgeClass: 'bg-emerald-50 border-emerald-200 text-emerald-700' };
  }
  if (statuses.some(s => ['completed', 'filled', 'done'].includes(s))) {
    return { filled: true, label: 'Filled', badgeClass: 'bg-emerald-50 border-emerald-200 text-emerald-700' };
  }
  if (statuses.some(s => ['sent', 'viewed', 'viewed_by_customer'].includes(s))) {
    return { filled: true, label: 'Sent', badgeClass: 'bg-blue-50 border-blue-200 text-blue-700' };
  }
  // draft instance exists -> treat as in-progress (created, not yet completed)
  return { filled: true, label: 'In Progress', badgeClass: 'bg-amber-50 border-amber-200 text-amber-700' };
}

/**
 * AttachCustomForm — universal dialog letting a user pick a Custom Form template,
 * auto-fill it with related-record data, edit, and save as a Form Instance attached
 * to the parent record.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - linkedType: 'estimate' | 'invoice' | 'job' | 'permit' | 'contact' | 'project'
 *   - linkedId: string  — id of the parent record
 *   - context: { customer_id?, project_id?, estimate_id?, invoice_id?, job_id?, permit_id? }
 *   - categoryFilter?: string  (e.g. 'invoice', 'permit') to limit picker to relevant templates
 *   - title?: string
 */
export default function AttachCustomForm({
  open,
  onClose,
  linkedType,
  linkedId,
  context = {},
  categoryFilter = null,
  county = null,
  title = 'Custom Forms',
}) {
  const [step, setStep] = useState('list'); // 'list' | 'filler'
  const [templates, setTemplates] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState(null);

  const loadData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = {};
      if (categoryFilter) params.category = categoryFilter;
      if (county) params.county = county;
      const [tplRes, instRes] = await Promise.all([
        api.get('/custom-forms/templates', { params }),
        api.get('/custom-forms/instances', {
          params: { linked_type: linkedType, linked_id: linkedId },
        }),
      ]);
      setTemplates((tplRes.data || []).filter(t => t.is_active));
      setInstances(instRes.data || []);
    } catch {
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  }, [open, linkedType, linkedId, categoryFilter, county]);

  useEffect(() => { loadData(); }, [loadData]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setStep('list');
      setSelectedTemplateId(null);
      setSelectedInstanceId(null);
    }
  }, [open]);

  const startNew = (templateId) => {
    setSelectedTemplateId(templateId);
    setSelectedInstanceId(null);
    setStep('filler');
  };

  const openInstance = (inst) => {
    setSelectedTemplateId(inst.template_id);
    setSelectedInstanceId(inst.id);
    setStep('filler');
  };

  const deleteInstance = async (instId) => {
    if (!window.confirm('Delete this filled form?')) return;
    try {
      await api.delete(`/custom-forms/instances/${instId}`);
      toast.success('Form deleted');
      loadData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose && onClose()}>
      <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b flex-shrink-0">
          <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#003366]" />
            {step === 'filler' && (
              <button
                type="button"
                onClick={() => { setStep('list'); setSelectedTemplateId(null); setSelectedInstanceId(null); }}
                className="text-sm text-gray-500 hover:text-[#003366] flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {step === 'list' ? (
            loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-[#003366]" />
              </div>
            ) : (
              <div className="p-4 sm:p-6 space-y-3 overflow-y-auto h-full">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700">Forms</h3>
                  <div className="flex items-center gap-2 text-[11px] text-gray-400">
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Filled / Signed</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300" /> Pending</span>
                  </div>
                </div>

                {templates.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="py-12 text-center text-gray-400">
                      <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm font-medium">No active templates available</p>
                      <p className="text-xs mt-1">Create one in the Custom Forms section.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden bg-white">
                    {templates.map(tpl => {
                      const tplInstances = instances.filter(i => i.template_id === tpl.id);
                      const latest = tplInstances[0] || null;
                      const status = computeFormStatus(tplInstances);
                      return (
                        <div
                          key={tpl.id}
                          data-testid={`form-row-${tpl.id}`}
                          className="flex items-center gap-3 px-3 sm:px-4 py-3 hover:bg-gray-50/70 transition-colors"
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${status.filled ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                            <FileText className="w-4.5 h-4.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-gray-900 text-sm truncate">{tpl.name}</p>
                              <span
                                data-testid={`form-status-${tpl.id}`}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${status.badgeClass}`}
                              >
                                {status.label}
                              </span>
                              {tplInstances.length > 1 && (
                                <span className="text-[10px] text-gray-400">×{tplInstances.length}</span>
                              )}
                            </div>
                            <p className="text-[11px] text-gray-400 capitalize mt-0.5">
                              {tpl.category}
                              {latest ? ` · ${new Date(latest.updated_at || latest.created_at).toLocaleDateString()}` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {latest ? (
                              <>
                                <Button
                                  size="sm" variant="outline" className="h-8 text-xs"
                                  onClick={() => openInstance(latest)}
                                  data-testid={`open-form-${tpl.id}`}
                                >
                                  Open
                                </Button>
                                <Button
                                  size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#003366]"
                                  title="Create another"
                                  onClick={() => startNew(tpl.id)}
                                  data-testid={`create-another-${tpl.id}`}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600"
                                  onClick={(e) => { e.stopPropagation(); deleteInstance(latest.id); }}
                                  data-testid={`delete-form-${tpl.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm" className="h-8 text-xs bg-[#003366] hover:bg-[#004080] text-white"
                                onClick={() => startNew(tpl.id)}
                                data-testid={`create-form-${tpl.id}`}
                              >
                                <Plus className="w-3.5 h-3.5 mr-1" /> Create
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )
          ) : (
            <FormFiller
              templateId={selectedTemplateId}
              instanceId={selectedInstanceId}
              context={context}
              linkedType={linkedType}
              linkedId={linkedId}
              onSaved={() => {
                loadData();
                setStep('list');
                setSelectedTemplateId(null);
                setSelectedInstanceId(null);
              }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
